import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import Card from '../components/Card';
import OnboardingLoading from './OnboardingLoading';

function Onboarding() {
  const navigate = useNavigate();
  const [responses, setResponses] = useState({
    phq1: null,
    phq2: null,
    gad1: null,
    gad2: null,
    sleep: null,
    social: null,
    exercise: null
  });
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [showLoading, setShowLoading] = useState(false);
  const [skipped, setSkipped] = useState(false);

  // Scroll to top when component mounts or question changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentQuestion]);

  const questions = [
    {
      id: 'phq1',
      text: 'In the last 2 weeks, how often have you been bothered by feeling down, depressed, or hopeless?',
      options: ["Not at all", "Several days", "More than half the days", "Nearly every day"]
    },
    {
      id: 'phq2',
      text: 'In the last 2 weeks, how often have you been bothered by little interest or pleasure in doing things?',
      options: ["Not at all", "Several days", "More than half the days", "Nearly every day"]
    },
    {
      id: 'gad1',
      text: 'In the last 2 weeks, how often have you been bothered by feeling nervous, anxious, or on edge?',
      options: ["Not at all", "Several days", "More than half the days", "Nearly every day"]
    },
    {
      id: 'gad2',
      text: 'In the last 2 weeks, how often have you been bothered by not being able to stop or control worrying?',
      options: ["Not at all", "Several days", "More than half the days", "Nearly every day"]
    },
    {
      id: 'sleep',
      text: 'How would you rate your sleep quality over the past week?',
      options: ["Excellent", "Good", "Fair", "Poor"]
    },
    {
      id: 'social',
      text: 'How often do you connect with friends or family for support?',
      options: ["Daily", "Several times a week", "Once a week", "Rarely"]
    },
    {
      id: 'exercise',
      text: 'How often do you engage in physical activity?',
      options: ["Daily", "3-4 times a week", "1-2 times a week", "Rarely"]
    }
  ];

  const handleAnswer = (value) => {
    setResponses(prev => ({
      ...prev,
      [questions[currentQuestion].id]: value
    }));
    
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handleSkip = () => {
    setSkipped(true);
    // Store minimal profile for skipped onboarding
    localStorage.setItem('userProfile', JSON.stringify({
      riskLevel: "moderate",
      recommendations: ["Complete onboarding for personalized insights", "Try daily check-ins", "Explore wellness resources"],
      initialScores: {},
      onboardingDate: new Date().toISOString(),
      skipped: true
    }));
    setShowLoading(true);
  };

  const handleRemindLater = () => {
    // Store reminder flag
    localStorage.setItem('onboardingReminder', 'true');
    navigate('/home');
  };

  const handleSubmit = () => {
    // Check if all required questions are answered
    const requiredAnswered = responses.phq1 !== null && responses.phq2 !== null && 
                            responses.gad1 !== null && responses.gad2 !== null;
    
    if (!requiredAnswered && !skipped) {
      alert('Please answer the required questions (marked with *)');
      return;
    }

    if (!skipped) {
      // Calculate risk level based on PHQ/GAD responses
      const phqScore = parseInt(responses.phq1) + parseInt(responses.phq2);
      const gadScore = parseInt(responses.gad1) + parseInt(responses.gad2);
      const totalScore = phqScore + gadScore;
      
      let riskLevel, recommendations;
      
      if (totalScore <= 2) {
        riskLevel = "low";
        recommendations = [
          "Daily mindfulness practice", 
          "Maintain social connections", 
          "Regular exercise",
          "Track sleep patterns",
          "Set weekly wellness goals"
        ];
      } else if (totalScore <= 4) {
        riskLevel = "moderate"; 
        recommendations = [
          "Try breathing exercises", 
          "Connect with peer support", 
          "Monitor mood daily",
          "Improve sleep hygiene",
          "Schedule social activities"
        ];
      } else {
        riskLevel = "elevated";
        recommendations = [
          "Immediate peer support session", 
          "Daily check-ins with BridgeBot", 
          "Consider professional resources",
          "Crisis support resources available",
          "Daily wellness check-ins"
        ];
      }
  
      // Add personalized recommendations based on lifestyle answers
      if (responses.sleep === "3" || responses.sleep === "2") { // Poor or Fair
        recommendations.push("Focus on sleep improvement strategies");
      }
      
      if (responses.social === "3" || responses.social === "2") { // Rarely or Once a week
        recommendations.push("Increase social connection activities");
      }
      
      if (responses.exercise === "3" || responses.exercise === "2") { // Rarely or 1-2 times
        recommendations.push("Incorporate more physical activity");
      }
  
      // Store personalized data
      localStorage.setItem('userProfile', JSON.stringify({
        riskLevel,
        recommendations,
        initialScores: { phqScore, gadScore },
        lifestyle: {
          sleep: responses.sleep,
          social: responses.social,
          exercise: responses.exercise
        },
        onboardingDate: new Date().toISOString(),
        skipped: false
      }));
    }
    
    setShowLoading(true);
  };
  
  if (showLoading) {
    return <OnboardingLoading />;
  }

  const currentQ = questions[currentQuestion];

  return (
    <div style={{ maxWidth: '480px', margin: 'auto', padding: '20px', backgroundColor: '#f5f7fa', minHeight: '100vh' }}>
      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        <h1 style={{ color: '#2c3e50', fontSize: '24px', marginBottom: '8px' }}>Welcome to MindBridge</h1>
        <p style={{ color: '#7f8c8d', fontSize: '14px' }}>
          Question {currentQuestion + 1} of {questions.length}
        </p>
        <div style={{ 
          width: '100%', 
          height: '4px', 
          backgroundColor: '#e2e8f0', 
          borderRadius: '2px',
          margin: '12px 0'
        }}>
          <div 
            style={{ 
              height: '100%', 
              width: `${((currentQuestion + 1) / questions.length) * 100}%`, 
              backgroundColor: '#4361ee',
              borderRadius: '2px',
              transition: 'width 0.3s ease'
            }} 
          />
        </div>
      </div>

      <Card>
        <h2 style={{ color: '#2c3e50', marginBottom: '20px', fontSize: '20px' }}>
          {currentQuestion < 4 ? '* ' : ''}{currentQ.text}
          {currentQuestion < 4 && <span style={{ fontSize: '14px', color: '#7f8c8d' }}> (Required)</span>}
        </h2>

        <div style={{ marginBottom: '24px' }}>
          {currentQ.options.map((option, i) => (
            <label 
              key={i} 
              style={{ 
                display: 'block', 
                marginBottom: '12px', 
                cursor: 'pointer',
                padding: '12px',
                borderRadius: '8px',
                border: responses[currentQ.id] === i.toString() ? '2px solid #4361ee' : '1px solid #e2e8f0',
                backgroundColor: responses[currentQ.id] === i.toString() ? '#eef2ff' : 'white',
                transition: 'all 0.2s ease'
              }}
              onClick={() => handleAnswer(i.toString())}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  border: '2px solid #cbd5e1',
                  marginRight: '12px',
                  backgroundColor: responses[currentQ.id] === i.toString() ? '#4361ee' : 'white'
                }} />
                <span style={{ fontSize: '16px', color: '#334155' }}>{option}</span>
              </div>
            </label>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
          {currentQuestion > 0 && (
            <Button 
              onClick={() => setCurrentQuestion(currentQuestion - 1)}
              variant="outline"
              style={{ flex: 1 }}
            >
              ← Back
            </Button>
          )}
          
          {currentQuestion < questions.length - 1 ? (
            <Button 
              onClick={() => {
                if (responses[currentQ.id] !== null) {
                  setCurrentQuestion(currentQuestion + 1);
                } else {
                  alert('Please select an answer');
                }
              }}
              disabled={responses[currentQ.id] === null}
              style={{ flex: currentQuestion > 0 ? 2 : 3 }}
            >
              Next Question →
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit}
              style={{ flex: currentQuestion > 0 ? 2 : 3 }}
            >
              Complete Setup
            </Button>
          )}
        </div>
      </Card>

      <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
        <Button 
          onClick={handleSkip}
          variant="outline"
          size="sm"
          style={{ flex: 1 }}
        >
          Skip for Now
        </Button>
        <Button 
          onClick={handleRemindLater}
          variant="outline"
          size="sm"
          style={{ flex: 1 }}
        >
          Remind Me Later
        </Button>
      </div>
    </div>
  );
}

export default Onboarding;
